// ─────────────────────────────────────────────────────────────────────────────
// Form question generator
//
// Turns the user's wizard selections (industry + services + goal + add-ons) into
// `Routemize_DTOs_BookingFormQuestionDto[]` ready to merge into the booking-form
// builder's `apiCustomQuestions` state.
//
// The `optionsJson` serialization here intentionally mirrors
// `stringifyFieldOptions` in CreateFormModal.tsx so generated questions behave
// identically to hand-built ones (options, request-only pre-qualifiers,
// radio display type, and conditional branching via stable branchKeys).
// ─────────────────────────────────────────────────────────────────────────────

import type { Routemize_DTOs_BookingFormQuestionDto } from '../../api/models/Routemize_DTOs_BookingFormQuestionDto';
import { randomUuid } from '../../utils/randomId';
import {
  getAddOnsForIndustry,
  getBudgetOptions,
  getIndustryTemplate,
  getProjectQuestionLabel,
  type AddOnQuestion,
  type TemplateOption,
  type TemplateQuestion,
  type TemplateQuestionType,
} from './industryQuestionTemplates';

export type FormGoal = 'bookings' | 'qualified';

/**
 * A single-service operator's project question would otherwise be a one-answer
 * formality, and anyone whose job doesn't match it has nowhere to go. In that
 * case we add an escape-hatch answer that opens a description box, so the
 * off-menu request still reaches them instead of bouncing.
 */
export const SOMETHING_ELSE_LABEL = 'Something else';

export const SOMETHING_ELSE_QUESTION: TemplateQuestion = {
  id: 'something_else_details',
  label: 'Tell us what you need',
  type: 'textarea',
  required: true,
};

export const offersSomethingElse = (selectedServiceCount: number): boolean =>
  selectedServiceCount === 1;

export interface GenerateQuestionsInput {
  industryId: string;
  /** Service ids (from the industry's project question) the business offers. */
  selectedServiceIds: string[];
  goal: FormGoal;
  /** Per-service follow-up ids the user chose to exclude in the review step. */
  disabledFollowUpServiceIds?: string[];
  /**
   * Include the optional "add a photo of the leak" questions hanging off repair
   * answers. On by default; the review step can switch them off.
   */
  includePhotoPrompts?: boolean;
  /** Common add-on question ids to include. */
  enabledAddOnIds: string[];
  /** When editing an existing form, stamp the new questions with its id. */
  bookingFormId?: string | null;
}

// UI type -> API FieldType. Mirrors `uiToFieldType` in CreateFormModal.
const UI_TO_FIELD_TYPE: Record<TemplateQuestionType, string> = {
  text: 'ShortText',
  textarea: 'Paragraph',
  select: 'Dropdown',
  radio: 'Checkbox',
  checkbox: 'Checkbox',
  file: 'Attachments',
};

interface SerializeArgs {
  options: string[];
  requestOnlyIndexes?: number[];
  displayType?: 'radio';
  optionTargets?: Record<string, string>;
  branchKey?: string;
}

// Mirrors CreateFormModal.stringifyFieldOptions output shape.
function serializeOptionsJson(args: SerializeArgs): string {
  const { options, requestOnlyIndexes = [], displayType, optionTargets, branchKey } = args;
  const obj: Record<string, unknown> = { options, requestOnlyIndexes };
  if (displayType === 'radio') obj.displayType = 'radio';
  if (branchKey) obj.branchKey = branchKey;
  if (optionTargets) {
    const cleaned: Record<string, string> = {};
    Object.entries(optionTargets).forEach(([k, v]) => {
      const idx = Number(k);
      if (!Number.isFinite(idx) || idx < 0 || idx >= options.length) return;
      if (!v) return;
      cleaned[String(idx)] = v;
    });
    if (Object.keys(cleaned).length > 0) obj.optionTargets = cleaned;
  }
  return JSON.stringify(obj);
}

function newId(): string {
  // Any id not prefixed with `existing_` is treated as a create by buildFormData.
  return `ai_${randomUuid()}`;
}

// Indexes of options to mark request-only, given the goal.
// `inheritRequestOnly` is for follow-ups hanging off a low-intent parent
// service: the customer is already on a review path, so none of the
// drill-down answers should offer instant booking.
function requestOnlyIndexesFor(
  options: TemplateOption[],
  goal: FormGoal,
  inheritRequestOnly = false,
): number[] {
  if (goal !== 'qualified') return [];
  if (inheritRequestOnly) return options.map((_, i) => i);
  return options
    .map((opt, i) => (opt.lowIntent ? i : -1))
    .filter(i => i >= 0);
}

/**
 * Build the API question DTOs for a wizard run.
 *
 * Order produced:
 *   1. The project-type question (radio) with conditional branches.
 *   2. Each selected service's follow-up question (conditional targets), each
 *      followed by any photo question its answers ask for.
 *   3. Enabled add-on questions, common or industry-specific (always visible).
 *
 * `order` is assigned 1..N here; the builder re-derives final order from its
 * display ordering at save time, so callers only need to keep these contiguous.
 */
export function generateFormQuestions(
  input: GenerateQuestionsInput,
): Routemize_DTOs_BookingFormQuestionDto[] {
  const template = getIndustryTemplate(input.industryId);
  if (!template) return [];

  const disabled = new Set(input.disabledFollowUpServiceIds ?? []);
  const bookingFormId = input.bookingFormId ?? null;

  const selectedServices = template.projectQuestion.services.filter(s =>
    input.selectedServiceIds.includes(s.id),
  );
  if (selectedServices.length === 0) return [];

  const questions: Routemize_DTOs_BookingFormQuestionDto[] = [];
  let order = 1;

  // 2 (built first so we can wire branchKeys into the project question).
  const followUpQuestions: Routemize_DTOs_BookingFormQuestionDto[] = [];
  // Map: project-question option index -> follow-up branchKey.
  const optionTargets: Record<string, string> = {};

  const includePhotos = input.includePhotoPrompts ?? true;

  selectedServices.forEach((service, optionIndex) => {
    const followUp = service.followUp;
    if (!followUp || disabled.has(service.id)) return;

    const branchKey = randomUuid();
    optionTargets[String(optionIndex)] = branchKey;

    // A photo question per answer that asks for one, wired to that answer the
    // same way the follow-up itself is wired to the service: the customer only
    // ever sees the one prompt matching the problem they picked.
    const photoTargets: Record<string, string> = {};
    const photoQuestions: Routemize_DTOs_BookingFormQuestionDto[] = [];
    if (includePhotos && followUp.type === 'radio') {
      (followUp.options ?? []).forEach((option, answerIndex) => {
        if (!option.photoPrompt) return;
        const photoBranchKey = randomUuid();
        photoTargets[String(answerIndex)] = photoBranchKey;
        photoQuestions.push(buildPhotoQuestion(option.photoPrompt, bookingFormId, photoBranchKey));
      });
    }

    followUpQuestions.push(
      buildQuestion(
        followUp,
        input.goal,
        bookingFormId,
        branchKey,
        photoTargets,
        !!service.lowIntent,
      ),
    );
    followUpQuestions.push(...photoQuestions);
  });

  // 1. Project-type question (radio controller).
  const serviceOptions: TemplateOption[] = selectedServices.map(s => ({
    label: s.label,
    lowIntent: s.lowIntent,
  }));

  // Escape hatch for single-service operators. Low-intent by design: an
  // off-menu job is exactly the kind that should be looked at before it lands
  // on the calendar.
  if (offersSomethingElse(selectedServices.length)) {
    const branchKey = randomUuid();
    optionTargets[String(serviceOptions.length)] = branchKey;
    serviceOptions.push({ label: SOMETHING_ELSE_LABEL, lowIntent: true });
    followUpQuestions.push(
      buildQuestion(SOMETHING_ELSE_QUESTION, input.goal, bookingFormId, branchKey),
    );
  }

  const projectQuestion: Routemize_DTOs_BookingFormQuestionDto = {
    id: newId(),
    bookingFormId,
    label: getProjectQuestionLabel(template, input.selectedServiceIds),
    type: 'Checkbox' as Routemize_DTOs_BookingFormQuestionDto['type'],
    isRequired: true,
    order: order++,
    optionsJson: serializeOptionsJson({
      options: serviceOptions.map(o => o.label),
      requestOnlyIndexes: requestOnlyIndexesFor(serviceOptions, input.goal),
      displayType: 'radio',
      optionTargets,
    }),
  };
  questions.push(projectQuestion);

  // Append follow-ups (already built above) with sequential order.
  followUpQuestions.forEach(q => {
    q.order = order++;
    questions.push(q);
  });

  // 3. Common add-ons + this industry's own upsells.
  const addOnById = new Map<string, AddOnQuestion>(
    getAddOnsForIndustry(input.industryId).map(a => [a.id, a]),
  );
  input.enabledAddOnIds.forEach(id => {
    const addOn = addOnById.get(id);
    if (!addOn) return;
    // Budget options are scaled to the industry so we don't offer mismatched
    // tiers (e.g. $10k+ for lawn maintenance).
    const resolved: AddOnQuestion =
      addOn.id === 'budget'
        ? { ...addOn, options: getBudgetOptions(input.industryId) }
        : addOn;
    const q = buildQuestion(resolved, input.goal, bookingFormId);
    q.order = order++;
    questions.push(q);
  });

  return questions;
}

function buildQuestion(
  q: TemplateQuestion,
  goal: FormGoal,
  bookingFormId: string | null,
  branchKey?: string,
  optionTargets?: Record<string, string>,
  inheritRequestOnly = false,
): Routemize_DTOs_BookingFormQuestionDto {
  const apiType = (UI_TO_FIELD_TYPE[q.type] ?? 'ShortText') as Routemize_DTOs_BookingFormQuestionDto['type'];
  const options = q.options ?? [];

  let optionsJson: string | null = null;
  if (q.type === 'radio' || q.type === 'select' || q.type === 'checkbox') {
    optionsJson = serializeOptionsJson({
      options: options.map(o => o.label),
      requestOnlyIndexes: requestOnlyIndexesFor(options, goal, inheritRequestOnly),
      displayType: q.type === 'radio' ? 'radio' : undefined,
      optionTargets,
      branchKey,
    });
  } else if (branchKey) {
    // Free-text questions carry no options, but a branched one still needs its
    // branchKey so the controlling answer can point at it.
    optionsJson = serializeOptionsJson({ options: [], branchKey });
  }

  return {
    id: newId(),
    bookingFormId,
    label: q.label,
    type: apiType,
    // For "qualified bookings" we want every question answered, so make them all
    // required by default. For "more bookings" keep the template's own setting
    // (mostly optional) to maximize form completion. Uploads stay optional
    // either way — a missing photo should never block a booking.
    isRequired: q.type === 'file' ? false : goal === 'qualified' ? true : (q.required ?? false),
    order: 0,
    optionsJson,
  };
}

// Photo upload shown only after a specific answer. Always optional.
function buildPhotoQuestion(
  label: string,
  bookingFormId: string | null,
  branchKey: string,
): Routemize_DTOs_BookingFormQuestionDto {
  return {
    id: newId(),
    bookingFormId,
    label,
    type: 'Attachments' as Routemize_DTOs_BookingFormQuestionDto['type'],
    isRequired: false,
    order: 0,
    // Attachment questions carry no options, but they still need a branchKey so
    // the controlling answer can point at them.
    optionsJson: JSON.stringify({ options: [], requestOnlyIndexes: [], branchKey }),
  };
}
